// frontend/app/components/lessonPlans/useLessonUpload.js
//
// The logic behind "upload a lesson file": hidden file input, parse, size
// check, API import, example download and the error list. Presentation-free so
// the admin row (LessonUploadButton) and the teacher "Start a new lesson" card
// (StartLessonCard) can each drive it from their own buttons.
//
// Web only in practice: a file picker needs a DOM <input type="file">. Callers
// decide what to render on native; the hook itself is safe to call anywhere.

import { useCallback, useRef, useState } from "react";

import {
  apiGetLessonImportExample,
  apiImportLessonPlan,
} from "../../lessonPlans";
import { saveJsonFile } from "../../utils/downloadJsonFile";

// Same ceiling the server enforces; checking here saves the round trip.
export const MAX_LESSON_FILE_BYTES = 1_000_000;

export const EXAMPLE_FILE_NAME = "lesson-example.json";

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

/** The rejection from apiImportLessonPlan as a list of display lines. */
const errorLinesOf = (err, t) => {
  if (err?.status === 413) return [t("This file is larger than 1 MB.")];
  if (Array.isArray(err?.detail) && err.detail.length > 0) {
    return err.detail.map((item) =>
      item?.path ? `${item.path}: ${item.message}` : String(item?.message ?? "")
    );
  }
  return [err?.message || t("The lesson could not be uploaded.")];
};

const useLessonUpload = ({ fetchWithAuth, onImported, t = (s) => s }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]);
  const [downloading, setDownloading] = useState(false);

  const openPicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const clearErrors = useCallback(() => setErrors([]), []);

  const handleFile = async (event) => {
    const input = event?.target;
    const file = input?.files?.[0];
    // Picking the same file twice must fire change twice.
    if (input) input.value = "";
    if (!file) return;

    setErrors([]);
    if (file.size > MAX_LESSON_FILE_BYTES) {
      setErrors([t("This file is larger than 1 MB.")]);
      return;
    }

    let parsed;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      setErrors([t("This file is not valid JSON.")]);
      return;
    }
    if (!isPlainObject(parsed)) {
      setErrors([t("A lesson file must contain one JSON object.")]);
      return;
    }

    setBusy(true);
    try {
      const result = await apiImportLessonPlan(fetchWithAuth, parsed);
      if (typeof onImported === "function") {
        onImported(result?.lesson, result?.warnings ?? []);
      }
    } catch (err) {
      setErrors(errorLinesOf(err, t));
    } finally {
      setBusy(false);
    }
  };

  const downloadExample = async () => {
    setErrors([]);
    setDownloading(true);
    try {
      const example = await apiGetLessonImportExample(fetchWithAuth);
      saveJsonFile(JSON.stringify(example, null, 2), EXAMPLE_FILE_NAME);
    } catch (err) {
      setErrors(errorLinesOf(err, t));
    } finally {
      setDownloading(false);
    }
  };

  return {
    inputRef,
    busy,
    downloading,
    errors,
    clearErrors,
    openPicker,
    handleFile,
    downloadExample,
  };
};

export default useLessonUpload;
